import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { trpc, trpcClient, trpcUnbatched } from '@/api/trpc';
import { withMutationTimeout } from './mutationTimeout';
import type {
    AeroCandidateAggregatedResultsInput,
    AeroCandidateAllResultsInput,
    AeroCandidateTopResultsInput,
    AeroOptimizationJobInput,
    AeroOptimizationJobsInput,
    BestExitCandidateBacktestInput,
    CandidateCoverageInput,
    CreateExitCandidateInput,
    FetchStrategiesOutput,
    MaterializeBaseStrategyCandidateInput,
    PublishAndAttachTradeLogicAuthoringDraftInput,
    PublishTradeLogicAuthoringDraftInput,
    PreviewBaseStrategyCandidateInput,
    PreviewExitCandidateDocumentInput,
    TradeLogicDefinitionSummary,
    TradeLogicAuthoringBackendStatus,
    TradeLogicAuthoringBackendStatusInput,
    TradeLogicVersionSummary,
    LoadPublishedTradeLogicDraftInput
} from './types';
import { createTradeLogicAuthoringBackendStatus } from './backendStatus';
import type { TradeLogicEditorContext, TradeLogicEditorKind } from '../tradeLogicNavigation';
import { hasStrategyTradeLogicApplyTarget, type PublishTradeLogicAuthoringDraftInContextResult } from './publishContext';

const AERO_CREATE_JOB_TIMEOUT_MS = 15000;

const AERO_CREATE_JOB_TIMEOUT_MESSAGE =
    'Timed out waiting for the AERO orchestrator. Confirm the Rust AERO service is running and try again.';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isTradeLogicDefinitionSummary = (value: unknown): value is TradeLogicDefinitionSummary =>
    isRecord(value) &&
    typeof value.definitionId === 'number' &&
    typeof value.name === 'string' &&
    typeof value.kind === 'string' &&
    typeof value.scope === 'string' &&
    (value.ownerType === 'library' || value.ownerType === 'strategy');

const isTradeLogicVersionSummary = (value: unknown): value is TradeLogicVersionSummary =>
    isRecord(value) && typeof value.definitionId === 'number' && typeof value.version === 'number' && typeof value.versionId === 'number';

const normalizeTradeLogicDefinitionSummaries = (value: unknown): TradeLogicDefinitionSummary[] =>
    Array.isArray(value) ? value.filter(isTradeLogicDefinitionSummary) : [];

const normalizeTradeLogicVersionSummaries = (value: unknown): TradeLogicVersionSummary[] =>
    Array.isArray(value) ? value.filter(isTradeLogicVersionSummary) : [];

export const updateCachedStrategyTradeLogicAttachment = (
    queryClient: QueryClient,
    {
        kind,
        strategyId,
        versionId
    }: {
        kind: 'entry' | 'exit';
        strategyId: number;
        versionId: number;
    }
) => {
    queryClient.setQueriesData<FetchStrategiesOutput>(trpcUnbatched.strategy.fetchStrategies.pathFilter(), (current) => {
        if (!current?.strategies) return current;

        return {
            ...current,
            strategies: current.strategies.map((strategy) =>
                strategy.id === strategyId
                    ? {
                          ...strategy,
                          ...(kind === 'entry' ? { tradeLogicV2EntryVersionId: versionId } : { tradeLogicV2ExitVersionId: versionId })
                      }
                    : strategy
            )
        };
    });
};

export const invalidatePublishedTradeLogicQueries = async (queryClient: QueryClient) => {
    await Promise.all([
        queryClient.invalidateQueries(trpc.tradeLogicAuthoring.loadPublishedDraft.pathFilter()),
        queryClient.invalidateQueries(trpcUnbatched.strategy.fetchTradeLogicDefinitions.pathFilter()),
        queryClient.invalidateQueries(trpcUnbatched.strategy.fetchTradeLogicDefinitionVersions.pathFilter()),
        queryClient.invalidateQueries(trpcUnbatched.strategy.fetchTradeLogicDefinitionVersionById.pathFilter()),
        queryClient.invalidateQueries(trpcUnbatched.strategy.fetchStrategies.pathFilter()),
        // Publishing and attaching a new version is how a strategy the runtime rejects gets fixed.
        queryClient.invalidateQueries(trpcUnbatched.strategy.fetchStrategyLiveReadiness.pathFilter())
    ]);
};

export const useTradeLogicAuthoringBackendStatus = ({
    strategyId,
    draftKind,
    registryManifestFingerprint
}: TradeLogicAuthoringBackendStatusInput): TradeLogicAuthoringBackendStatus => {
    return useMemo(
        () =>
            createTradeLogicAuthoringBackendStatus({
                draftKind,
                registryManifestFingerprint,
                strategyId
            }),
        [draftKind, registryManifestFingerprint, strategyId]
    );
};

export const useTradeLogicDefinitions = (params: {
    enabled?: boolean;
    isActiveOnly?: boolean;
    kind?: 'entry' | 'exit';
    scope?: TradeLogicDefinitionSummary['scope'];
}) => {
    const query = useQuery({
        ...trpcUnbatched.strategy.fetchTradeLogicDefinitions.queryOptions({
            kind: params.kind,
            isActiveOnly: params.isActiveOnly
        }),
        enabled: params.enabled ?? true,
        staleTime: 60000
    });

    const definitions = useMemo<TradeLogicDefinitionSummary[]>(() => {
        const rawDefinitions = normalizeTradeLogicDefinitionSummaries(query.data?.definitions);
        const scopedDefinitions = params.scope ? rawDefinitions.filter((definition) => definition.scope === params.scope) : rawDefinitions;

        return [...scopedDefinitions].sort((left, right) => left.name.localeCompare(right.name));
    }, [params.scope, query.data]);

    return {
        ...query,
        definitions
    };
};

export const useTradeLogicDefinitionVersions = (params: { definitionId: number | null; enabled?: boolean }) => {
    const query = useQuery({
        ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersions.queryOptions({
            definitionId: params.definitionId ?? 0
        }),
        enabled: (params.enabled ?? true) && params.definitionId !== null,
        staleTime: 60000
    });

    const versions = useMemo<TradeLogicVersionSummary[]>(() => {
        const rawVersions = normalizeTradeLogicVersionSummaries(query.data?.versions);

        return [...rawVersions].sort((left, right) => right.version - left.version);
    }, [query.data]);

    return {
        ...query,
        versions
    };
};

export interface TradingProfileScopeSummary {
    id: number;
    name: string;
}

export const useTradingProfileScopeSummaries = (params: { enabled?: boolean } = {}) => {
    const query = useQuery({
        ...trpc.user.fetchTradingProfiles.queryOptions(),
        enabled: params.enabled ?? true,
        staleTime: 60000
    });

    const tradingProfiles = useMemo<TradingProfileScopeSummary[]>(
        () =>
            (query.data?.tradingProfiles ?? [])
                .map((profile) => ({
                    id: profile.id,
                    name: profile.name
                }))
                .sort((left, right) => left.name.localeCompare(right.name)),
        [query.data?.tradingProfiles]
    );

    return {
        ...query,
        tradingProfiles
    };
};

export const useCandidateCoverage = (params: { input: CandidateCoverageInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.tradeLogicExitCandidates.getCandidateCoverage.queryOptions(
            params.input ?? {
                strategyId: 0,
                tradingProfileId: 0
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 30000
    });
};

export const useBestExitCandidateBacktest = (params: { input: BestExitCandidateBacktestInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.tradeLogicExitCandidates.getBestCandidateBacktest.queryOptions(
            params.input ?? {
                strategyId: 0,
                tradingProfileId: 0,
                entityId: 0,
                entityType: 'Asset'
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 30000
    });
};

export const usePreviewExecutableCandidateDocument = (params: { input: PreviewExitCandidateDocumentInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.tradeLogicExitCandidates.previewExecutableCandidateDocument.queryOptions(
            params.input ?? {
                strategyId: 0,
                candidateId: 0
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 60000
    });
};

export const usePreviewBaseStrategyCandidate = (params: { input: PreviewBaseStrategyCandidateInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.tradeLogicExitCandidates.previewBaseStrategyCandidate.queryOptions(
            params.input ?? {
                strategyId: 0,
                tradingProfileId: null
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 30000
    });
};

export const useValidateTradeLogicAuthoringDraftMutation = () => {
    return useMutation({
        ...trpc.tradeLogicAuthoring.validateDraft.mutationOptions()
    });
};

export const useCompileTradeLogicAuthoringDraftMutation = () => {
    return useMutation({
        ...trpc.tradeLogicAuthoring.compileDraft.mutationOptions()
    });
};

/**
 * Saves the editor's current rules onto a draft Idea Lab variant. Nothing is published: the variant
 * stays a detached snapshot until it is frozen in the Idea Lab bench.
 */
export const useSaveIdeaLabVariantRulesMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.ideaLab.updateVariant.mutationOptions(),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries(trpc.ideaLab.getVariant.pathFilter()),
                queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter())
            ]);
        }
    });
};

export const useSaveTradeLogicAuthoringDraftMutation = () => {
    return useMutation({
        ...trpc.tradeLogicAuthoring.saveDraft.mutationOptions()
    });
};

export const usePublishTradeLogicAuthoringDraftMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.tradeLogicAuthoring.publishDraft.mutationOptions({
            onSuccess: async (result) => {
                if (!result.published) return;

                await invalidatePublishedTradeLogicQueries(queryClient);
            }
        })
    });
};

export const usePublishAndAttachTradeLogicAuthoringDraftMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.tradeLogicAuthoring.publishAndAttachDraft.mutationOptions({
            onSuccess: async (result) => {
                if (!result.published) return;

                if (result.attachedStrategy !== null) {
                    updateCachedStrategyTradeLogicAttachment(queryClient, result.attachedStrategy);
                }

                await invalidatePublishedTradeLogicQueries(queryClient);
            }
        })
    });
};

export const usePublishTradeLogicAuthoringDraftInContextMutation = ({
    kind,
    saveContext
}: {
    kind: TradeLogicEditorKind;
    saveContext?: TradeLogicEditorContext;
}) => {
    const queryClient = useQueryClient();

    return useMutation<PublishTradeLogicAuthoringDraftInContextResult, Error, PublishTradeLogicAuthoringDraftInput>({
        mutationFn: async (input) => {
            if (!hasStrategyTradeLogicApplyTarget(saveContext)) {
                const publishResult = await trpcClient.tradeLogicAuthoring.publishDraft.mutate(input);

                return {
                    publishResult,
                    appliedToStrategy: false
                };
            }

            const publishResult = await trpcClient.tradeLogicAuthoring.publishAndAttachDraft.mutate({
                ...input,
                strategyId: saveContext.strategyId,
                draft: {
                    ...input.draft,
                    strategyId: saveContext.strategyId
                }
            } satisfies PublishAndAttachTradeLogicAuthoringDraftInput);

            if (publishResult.published && publishResult.attachedStrategy === null) {
                throw new Error('Trade Logic version was published but not attached to the strategy.');
            }

            return {
                publishResult,
                appliedToStrategy: publishResult.attachedStrategy?.kind === kind
            };
        },
        onSuccess: async (result) => {
            if (!result.publishResult.published) return;

            if (result.publishResult.attachedStrategy !== null) {
                updateCachedStrategyTradeLogicAttachment(queryClient, result.publishResult.attachedStrategy);
            }

            await invalidatePublishedTradeLogicQueries(queryClient);
        }
    });
};

export const useLoadPublishedTradeLogicDraftMutation = () => {
    return useMutation({
        mutationFn: (input: LoadPublishedTradeLogicDraftInput) => trpcClient.tradeLogicAuthoring.loadPublishedDraft.query(input)
    });
};

export const useLoadPublishedTradeLogicDraft = (params: { input: LoadPublishedTradeLogicDraftInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.tradeLogicAuthoring.loadPublishedDraft.queryOptions(params.input ?? { definitionId: 0 }),
        enabled: (params.enabled ?? true) && params.input !== null && params.input.definitionId > 0,
        staleTime: 30000
    });
};

export const useAeroCandidateTopResults = (params: { input: AeroCandidateTopResultsInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.exitOptimization.fetchTopResults.queryOptions(
            params.input ?? {
                jobId: 0,
                limit: 1
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 30000
    });
};

/**
 * Winning candidate per option-buying-strategy axis, ranked server-side across every candidate on
 * that axis. Deriving this from the paged results table compares axes on unequal samples.
 */
export const useAeroOptionBuyingStrategyLeaderboard = (params: {
    jobId: number | null;
    sortBy: 'selection' | 'avgProfit' | 'winsorizedAvgProfit' | 'score' | 'winsorizedScore';
    enabled?: boolean;
}) => {
    return useQuery({
        ...trpc.exitOptimization.fetchOptionBuyingStrategyLeaderboard.queryOptions({
            jobId: params.jobId ?? 0,
            sortBy: params.sortBy
        }),
        enabled: (params.enabled ?? true) && params.jobId !== null,
        staleTime: 30000
    });
};

export const useAeroCandidateAllResults = (params: { input: AeroCandidateAllResultsInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.exitOptimization.fetchAllResults.queryOptions(
            params.input ?? {
                jobId: 0
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 30000
    });
};

export const useAeroCandidateAggregatedResults = (params: { input: AeroCandidateAggregatedResultsInput | null; enabled?: boolean }) => {
    return useQuery({
        ...trpc.exitOptimization.fetchAggregatedResults.queryOptions(
            params.input ?? {
                jobId: 0,
                limit: 1,
                sortBy: 'selection'
            }
        ),
        enabled: (params.enabled ?? true) && params.input !== null,
        staleTime: 30000
    });
};

export const useAeroOptimizationJobs = (params: { input?: AeroOptimizationJobsInput; enabled?: boolean } = {}) => {
    return useQuery({
        ...trpc.exitOptimization.fetchJobs.queryOptions(params.input ?? {}),
        enabled: params.enabled ?? true,
        staleTime: 30000
    });
};

export const useCreateAeroOptimizationJobMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.exitOptimization.createJob.mutationOptions(),
        mutationFn: (variables: AeroOptimizationJobInput) =>
            withMutationTimeout(
                trpcClient.exitOptimization.createJob.mutate(variables),
                AERO_CREATE_JOB_TIMEOUT_MS,
                AERO_CREATE_JOB_TIMEOUT_MESSAGE
            ),
        onSuccess: async () => {
            await queryClient.invalidateQueries(trpc.exitOptimization.fetchJobs.pathFilter());
        }
    });
};

const invalidateGlobalExitCandidateQueries = async (queryClient: QueryClient) => {
    await Promise.all([
        queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.searchGlobalCandidates.pathFilter()),
        queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.pathFilter()),
        queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getCandidateCoverage.pathFilter()),
        queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getBestCandidateBacktest.pathFilter())
    ]);
};

export const useCreateExitCandidateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.tradeLogicExitCandidates.createCandidate.mutationOptions(),
        onSuccess: async (_data, variables: CreateExitCandidateInput) => {
            await invalidateGlobalExitCandidateQueries(queryClient);

            if (variables.strategyId !== undefined) {
                await queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.pathFilter());
            }
        }
    });
};

export const useMaterializeBaseStrategyCandidateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.tradeLogicExitCandidates.materializeBaseStrategyCandidate.mutationOptions(),
        onSuccess: async (_data, _variables: MaterializeBaseStrategyCandidateInput) => {
            await invalidateGlobalExitCandidateQueries(queryClient);
        }
    });
};

export const useQueueCandidateCoverageMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.tradeLogicExitCandidates.queueCandidateCoverage.mutationOptions(),
        onSuccess: async () => {
            await queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getCandidateCoverage.pathFilter());
            await queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getBestCandidateBacktest.pathFilter());
        }
    });
};

export const usePromoteAeroCandidatesToGlobalCandidatesMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        ...trpc.exitOptimization.promoteResultsToGlobalCandidates.mutationOptions(),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries(trpc.exitOptimization.fetchTopResults.pathFilter()),
                queryClient.invalidateQueries(trpc.exitOptimization.fetchAggregatedResults.pathFilter()),
                queryClient.invalidateQueries(trpc.exitOptimization.fetchAllResults.pathFilter()),
                queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.searchGlobalCandidates.pathFilter()),
                queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getCandidateCoverage.pathFilter()),
                queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getBestCandidateBacktest.pathFilter()),
                queryClient.invalidateQueries(trpc.exitOptimization.fetchOptionBuyingStrategyLeaderboard.pathFilter())
            ]);
        }
    });
};
